import { toast } from 'sonner';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import eyvoLogo from '../../assets/eyvo_report_logo.png';
import {
  DollarSign, Calendar, Hash, ArrowRightLeft, Trash2,
  TrendingUp, Package, Clock, ClipboardCheck, BarChart3, FileCheck
} from 'lucide-react';

export const fmtCurrency = (v) => (v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
export const fmtNum = (v) => (v || 0).toLocaleString('en-US', { minimumFractionDigits: 2 });
export const fmtDate = (d, tz) => {
  if (!d) return '—';
  try {
    const opts = { day: '2-digit', month: 'short', year: 'numeric' };
    if (tz) opts.timeZone = tz;
    return new Date(d).toLocaleDateString('en-GB', opts);
  }
  catch { return d; }
};

// ── CSV Export ──
export function exportCSV(rows, filename) {
  if (!rows.length) return;
  const keys = Object.keys(rows[0]).filter(k => k !== 'events');
  const csv = [keys.join(','), ...rows.map(r => keys.map(k => {
    let v = r[k];
    if (typeof v === 'object') v = JSON.stringify(v);
    if (typeof v === 'string' && (v.includes(',') || v.includes('"'))) v = `"${v.replace(/"/g, '""')}"`;
    return v ?? '';
  }).join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url;
  a.download = `${filename}_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click(); URL.revokeObjectURL(url);
  toast.success(`Exported ${rows.length} rows to CSV`);
}

// ── PDF Export ──
let _pdfLogoDataUrl = null;
const _pdfLogoImg = new Image();
_pdfLogoImg.onload = () => {
  const canvas = document.createElement('canvas');
  canvas.width = _pdfLogoImg.naturalWidth;
  canvas.height = _pdfLogoImg.naturalHeight;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(_pdfLogoImg, 0, 0);
  _pdfLogoDataUrl = canvas.toDataURL('image/png');
};
_pdfLogoImg.src = eyvoLogo;

export function exportPDF(title, columns, rows, summaryLines = [], filename, tz) {
  const doc = new jsPDF({ orientation: rows[0] && Object.keys(rows[0]).length > 6 ? 'landscape' : 'portrait' });
  const pageW = doc.internal.pageSize.getWidth();
  const year = new Date().getFullYear();
  const genOpts = { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' };
  if (tz) genOpts.timeZone = tz;
  const generatedAt = new Date().toLocaleString('en-GB', genOpts);

  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text(title, 14, 18);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.text(`Generated: ${generatedAt}`, 14, 24);

  if (_pdfLogoDataUrl) {
    try {
      const logoH = 12;
      const aspect = _pdfLogoImg.naturalWidth / _pdfLogoImg.naturalHeight;
      const logoW = logoH * aspect;
      doc.addImage(_pdfLogoDataUrl, 'PNG', pageW - logoW - 14, 6, logoW, logoH);
    } catch (e) {
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(30, 41, 59);
      doc.text('EYVO', pageW - 14, 15, { align: 'right' });
      doc.setTextColor(0, 0, 0);
    }
  }

  let startY = 35;
  if (summaryLines.length > 0) {
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    summaryLines.forEach((line, i) => {
      doc.text(line, 14, startY + i * 5);
    });
    startY += summaryLines.length * 5 + 5;
  }

  const head = [columns.map(c => c.label || c.header)];
  const body = rows.map(r => columns.map(c => {
    let val = r[c.key];
    if (c.pdfFmt) return c.pdfFmt(val);
    if (val === null || val === undefined) return '—';
    if (typeof val === 'boolean') return val ? 'Yes' : 'No';
    return String(val);
  }));

  let pageNum = 0;
  autoTable(doc, {
    head, body, startY,
    styles: { fontSize: 7, cellPadding: 1.5 },
    headStyles: { fillColor: [51, 65, 85], textColor: 255, fontStyle: 'bold', fontSize: 7 },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    margin: { left: 14, right: 14, top: 22 },
    didDrawPage: (data) => {
      pageNum++;
      const pageH = doc.internal.pageSize.getHeight();
      if (pageNum > 1) {
        doc.setFontSize(7);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(120);
        doc.text(`${title}  |  ${generatedAt}`, 14, 10);
        doc.setTextColor(0, 0, 0);
        if (_pdfLogoDataUrl) {
          try {
            const lH = 7;
            const asp = _pdfLogoImg.naturalWidth / _pdfLogoImg.naturalHeight;
            doc.addImage(_pdfLogoDataUrl, 'PNG', pageW - lH * asp - 14, 4, lH * asp, lH);
          } catch {}
        }
      }
      doc.setFontSize(7);
      doc.setTextColor(100);
      doc.text(`Page ${pageNum}`, pageW - 25, pageH - 8);
      doc.text(`\u00A9 ${year} Eyvo. All rights reserved.`, 14, pageH - 8);
      doc.setTextColor(80, 130, 200);
      doc.textWithLink('Powered by Eyvo', pageW / 2 - 12, pageH - 8, { url: 'https://www.eyvo.com' });
      doc.setTextColor(0, 0, 0);
    },
  });

  return doc;
}

export function downloadPDF(title, columns, rows, summaryLines = [], filename, tz) {
  const doc = exportPDF(title, columns, rows, summaryLines, filename, tz);
  doc.save(`${filename}_${new Date().toISOString().slice(0, 10)}.pdf`);
  toast.success('Exported to PDF');
}

// ── Report Definitions ──
export const REPORTS = [
  { id: 'stock-valuation', label: 'Stock Valuation', icon: DollarSign, group: 'Manager', desc: 'Total inventory value by location, item, and category' },
  { id: 'batch-expiry', label: 'Batch Expiry', icon: Calendar, group: 'Manager', desc: 'Batches by expiry urgency with financial exposure' },
  { id: 'serial-audit-trail', label: 'Serial Audit Trail', icon: Hash, group: 'Manager', desc: 'Full lifecycle history of serial numbers' },
  { id: 'stock-movements', label: 'Stock Movements', icon: ArrowRightLeft, group: 'Manager', desc: 'All movements over a date range by type' },
  { id: 'write-offs', label: 'Write-Off & Disposition', icon: Trash2, group: 'Both', desc: 'Scrapped, written-off, and returned items with values' },
  { id: 'replenishment-performance', label: 'Replenishment Performance', icon: TrendingUp, group: 'Manager', desc: 'Fill rates, minimum stock hits, reorder frequency' },
  { id: 'cost-of-goods-issued', label: 'Cost of Goods Issued', icon: Package, group: 'Accountant', desc: 'Value of stock issued by department' },
  { id: 'stock-aging', label: 'Stock Aging', icon: Clock, group: 'Accountant', desc: 'Value by age band for provision calculations' },
  { id: 'cycle-count-variance', label: 'Cycle Count Variance', icon: ClipboardCheck, group: 'Accountant', desc: 'Discrepancies from cycle count results' },
  { id: 'period-comparison', label: 'Period Comparison', icon: BarChart3, group: 'Accountant', desc: 'Opening vs closing stock with movement reconciliation' },
  { id: 'valuation-audit', label: 'Valuation Audit', icon: FileCheck, group: 'Both', desc: 'Complete item-by-item valuation audit with method breakdown — single source of truth for total inventory value' },
];

export { eyvoLogo };
